import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { QRCodeCanvas } from 'qrcode.react';
import RegisteredUsers from './RegisteredUsers';

import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  MenuItem,
} from '@mui/material';

import PersonIcon from '@mui/icons-material/Person';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const RegistrationForm = () => {

  const [form, setForm] = useState({
    name: '',
    vehicleNumber: '',
    qrCodeId: '',
    contact: '',
    whatsapp: '',
    emergencyContacts: [
      {
        name: '',
        number: '',
        relation: '',
      },
    ],
  });

  const [docId, setDocId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successOpen, setSuccessOpen] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [duplicateRecord, setDuplicateRecord] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrFromUrl, setQrFromUrl] = useState(false);
  const location = useLocation();

  const relationOptions = [
    'Father',
    'Mother',
    'Spouse',
    'Brother',
    'Sister',
    'Friend',
    'Colleague',
    'Relative',
    'Other',
  ];

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const qr = params.get('qr') || params.get('qrId');
      if (qr) {
        setForm((prev) => ({ ...prev, qrCodeId: qr }));
        setQrFromUrl(true);
      }
    } catch (e) {
      // ignore
    }
  }, [location.search]);

  const handleEmergencyChange = (idx, field, value) => {

    const updated = [...form.emergencyContacts];

    updated[idx][field] = value;

    setForm({
      ...form,
      emergencyContacts: updated,
    });
  };

  const addEmergencyContact = () => {

    setForm({
      ...form,
      emergencyContacts: [
        ...form.emergencyContacts,
        {
          name: '',
          number: '',
          relation: '',
        },
      ],
    });
  };

  const removeEmergencyContact = (idx) => {

    if (form.emergencyContacts.length === 1) return;

    const updated = form.emergencyContacts.filter(
      (_, i) => i !== idx
    );

    setForm({
      ...form,
      emergencyContacts: updated,
    });
  };

  // Validation
  const validate = () => {

    if (!form.name.trim()) {
      return 'Owner name is required.';
    }

    const vehicleRegex =
      /^[A-Z]{2}[0-9]{1,2}[A-Z]{1,3}[0-9]{4}$/i;

    if (
      !form.vehicleNumber.trim() ||
      !vehicleRegex.test(
        form.vehicleNumber.replace(/\s+/g, '')
      )
    ) {
      return 'Enter valid vehicle number.';
    }

    if (!/^[6-9]\d{9}$/.test(form.contact)) {
      return 'Enter valid contact number.';
    }

    if (!/^[6-9]\d{9}$/.test(form.whatsapp)) {
      return 'Enter valid WhatsApp number.';
    }

    for (let i = 0; i < form.emergencyContacts.length; i++) {

      const ec = form.emergencyContacts[i];

      if (!ec.name.trim()) {
        return `Emergency contact ${i + 1} name required`;
      }

      if (!/^[6-9]\d{9}$/.test(ec.number)) {
        return `Emergency contact ${i + 1} number invalid`;
      }

      if (!ec.relation.trim()) {
        return `Emergency contact ${i + 1} relation required`;
      }
    }

    return '';
  };

  const findDuplicate = async (normalizedVehicleNumber) => {
    const q = query(
      collection(db, 'registrations'),
      where('vehicleNumber', '==', normalizedVehicleNumber)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.length ? { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } : null;
  };

  const findByQrId = async (qrId) => {
    if (!qrId) return null;
    const q = query(
      collection(db, 'registrations'),
      where('qrCodeId', '==', qrId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.length ? { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errMsg = validate();
    if (errMsg) {
      setError(errMsg);
      return;
    }

    setLoading(true);
    setError('');
    setDuplicateRecord(null);

    try {
      const normalizedVehicleNumber = form.vehicleNumber.trim().toUpperCase().replace(/\s+/g, '');
      const existing = await findDuplicate(normalizedVehicleNumber);

      if (form.qrCodeId) {
        const existingQr = await findByQrId(form.qrCodeId.trim());
        if (existingQr && (!editMode || existingQr.id !== docId)) {
          setError('This QR is already linked to another vehicle.');
          setDuplicateRecord(existingQr);
          setDocId(existingQr.id);
          setLoading(false);
          return;
        }
      }

      if (existing && (!editMode || existing.id !== docId)) {
        setDuplicateRecord(existing);
        setDocId(existing.id);
        setForm({
          name: existing.name,
          vehicleNumber: existing.vehicleNumber,
          contact: existing.contact,
          whatsapp: existing.whatsapp,
          emergencyContacts: existing.emergencyContacts || [{ name: '', number: '', relation: '' }],
        });
        setError('This vehicle is already registered. You can edit the record or regenerate the QR.');
        setLoading(false);
        return;
      }

      const normalizedForm = {
        name: form.name,
        vehicleNumber: normalizedVehicleNumber,
        qrCodeId: form.qrCodeId ? form.qrCodeId.trim() : '',
        contact: form.contact,
        whatsapp: form.whatsapp,
        emergencyContacts: form.emergencyContacts,
      };

      if (editMode && docId) {
        await updateDoc(doc(db, 'registrations', docId), {
          ...normalizedForm,
          updatedAt: new Date(),
        });
        setSuccessOpen(true);
      } else {
        const docRef = await addDoc(collection(db, 'registrations'), {
          ...normalizedForm,
          createdAt: new Date(),
        });
        setDocId(docRef.id);
        setSuccessOpen(true);
        // If a pre-generated QR was used, mark it linked in qrcodes collection
        if (form.qrCodeId) {
          try {
            const q = query(collection(db, 'qrcodes'), where('qrId', '==', form.qrCodeId.trim()));
            const snap = await getDocs(q);
            if (!snap.empty) {
              const qDoc = snap.docs[0];
              await updateDoc(doc(db, 'qrcodes', qDoc.id), {
                linked: true,
                linkedRegistrationId: docRef.id,
                linkedVehicleNumber: normalizedVehicleNumber,
                linkedAt: new Date(),
              });
            }
          } catch (e) {
            // ignore
          }
        }
      }

      setForm({ ...form, vehicleNumber: normalizedVehicleNumber });
      setEditMode(false);
      setDuplicateRecord(null);
    } catch (err) {
      setError('Registration failed. Try again.');
    }

    setLoading(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const normalizedVehicleNumber = form.vehicleNumber.trim().toUpperCase().replace(/\s+/g, '');
  const finalQrUrl = form.qrCodeId
    ? `${window.location.origin}/app/#/alert/qr/${encodeURIComponent(form.qrCodeId)}`
    : docId
    ? `${window.location.origin}/app/#/alert/${encodeURIComponent(normalizedVehicleNumber)}`
    : '';

  const openQrDialog = () => {
    if (!docId) return;
    setQrDialogOpen(true);
  };

  const loadForEdit = () => {
    if (!duplicateRecord) return;
    setEditMode(true);
    setError('');
  };

  return (

    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        p: 2,
      }}
    >

      {!showUsers ? (

        <Card
          sx={{
            maxWidth: 650,
            width: '100%',
            p: 3,
            boxShadow: 6,
            borderRadius: 4,
          }}
        >

          <CardContent>

            {/* Heading */}
            <Typography
              variant="h4"
              align="center"
              fontWeight={700}
              gutterBottom
            >
              Vehicle Registration
            </Typography>

            <Typography
              variant="subtitle1"
              align="center"
              color="text.secondary"
              gutterBottom
            >
              Register your vehicle and emergency details
            </Typography>

            {/* Form */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ mt: 3 }}
            >

              <Grid container spacing={2}>

                {/* Owner Name */}
                <Grid item xs={12}>
                  <TextField
                    name="name"
                    label="Owner Name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Pre-generated QR ID (optional) */}
                <Grid item xs={12}>
                  <TextField
                    name="qrCodeId"
                    label="Pre-generated QR ID (optional)"
                    value={form.qrCodeId}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                      readOnly: qrFromUrl,
                    }}
                    helperText={form.qrCodeId ? (qrFromUrl ? '✓ QR detected from scanned link (read-only)' : 'QR detected from scanned link or entered manually') : 'If you scanned a pre-generated QR, it will appear here'}
                  />
                </Grid>

                {/* Vehicle Number */}
                <Grid item xs={12}>
                  <TextField
                    name="vehicleNumber"
                    label="Vehicle Number"
                    value={form.vehicleNumber}
                    onChange={handleChange}
                    required
                    fullWidth
                    helperText="e.g. MH14AB1234"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DirectionsCarIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Contact */}
                <Grid item xs={12}>
                  <TextField
                    name="contact"
                    label="Primary Contact Number"
                    value={form.contact}
                    onChange={handleChange}
                    required
                    fullWidth
                    type="tel"
                    helperText="For emergency alerts"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* WhatsApp */}
                <Grid item xs={12}>
                  <TextField
                    name="whatsapp"
                    label="WhatsApp Number"
                    value={form.whatsapp}
                    onChange={handleChange}
                    required
                    fullWidth
                    type="tel"
                    helperText="For WhatsApp notifications"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WhatsAppIcon color="success" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Emergency Contacts */}
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    sx={{ mt: 2 }}
                  >
                    Emergency Contact(s)
                  </Typography>
                </Grid>

                {form.emergencyContacts.map((ec, idx) => (

                  <React.Fragment key={idx}>

                    {/* Contact Name */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Contact Name"
                        value={ec.name}
                        onChange={(e) =>
                          handleEmergencyChange(
                            idx,
                            'name',
                            e.target.value
                          )
                        }
                        fullWidth
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon color="error" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    {/* Relation */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="Relation"
                        value={ec.relation}
                        onChange={(e) =>
                          handleEmergencyChange(
                            idx,
                            'relation',
                            e.target.value
                          )
                        }
                        fullWidth
                        required
                      >
                        {relationOptions.map((option) => (
                          <MenuItem
                            key={option}
                            value={option}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* Contact Number */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Emergency Contact Number"
                        value={ec.number}
                        onChange={(e) =>
                          handleEmergencyChange(
                            idx,
                            'number',
                            e.target.value
                          )
                        }
                        fullWidth
                        required
                        type="tel"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon color="error" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    {/* Remove Button */}
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {form.emergencyContacts.length > 1 && (
                        <Button
                          color="error"
                          onClick={() =>
                            removeEmergencyContact(idx)
                          }
                        >
                          Remove
                        </Button>
                      )}
                    </Grid>

                  </React.Fragment>

                ))}

                {/* Add Contact */}
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={addEmergencyContact}
                  >
                    + Add Emergency Contact
                  </Button>
                </Grid>

                {/* Submit */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={loading}
                    sx={{
                      mt: 2,
                      py: 1.5,
                      fontWeight: 600,
                    }}
                  >
                    {loading
                      ? editMode ? 'Updating...' : 'Registering...'
                      : editMode ? 'Update Registration' : 'Register Vehicle'}
                  </Button>
                </Grid>

                {duplicateRecord && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      This vehicle is already registered.
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button size="small" variant="outlined" onClick={loadForEdit}>
                          Edit Record
                        </Button>
                        <Button size="small" variant="contained" onClick={openQrDialog}>
                          Regenerate QR
                        </Button>
                      </Box>
                    </Alert>
                  </Grid>
                )}

              </Grid>

            </Box>

            {/* Error Snackbar */}
            {error && (
              <Snackbar
                open={!!error}
                autoHideDuration={4000}
                onClose={() => setError('')}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                <Alert severity="error">
                  {error}
                </Alert>
              </Snackbar>
            )}

            {/* Success Dialog */}
            <Dialog
              open={successOpen}
              onClose={() => {
                setSuccessOpen(false);
                setShowUsers(true);
              }}
              maxWidth="sm"
              fullWidth
            >

              <DialogTitle
                sx={{
                  textAlign: 'center',
                  fontWeight: 700,
                }}
              >
                Registration Successful!
              </DialogTitle>

              <DialogContent
                sx={{
                  textAlign: 'center',
                  px: 4,
                  py: 3,
                }}
              >

                {/* Business Name */}
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color="primary"
                >
                  GadiMitra
                </Typography>

                <Typography
                  variant="subtitle1"
                  gutterBottom
                >
                  Vehicle Owner Alert System
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ mb: 2 }}
                >
                  Smart QR Based Vehicle Safety Solution
                </Typography>

                {/* QR */}
                {docId && (
                  <Box sx={{ mt: 2 }}>
                    <QRCodeCanvas
                      value={finalQrUrl}
                      size={220}
                      includeMargin
                    />

                    <Typography
                      variant="body1"
                      sx={{
                        mt: 2,
                        fontWeight: 700,
                      }}
                    >
                      Vehicle No: {normalizedVehicleNumber}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ mt: 1 }}
                    >
                      Scan this code to view owner details and alert them instantly.
                    </Typography>

                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ mt: 1, wordBreak: 'break-all' }}
                    >
                      <a href={finalQrUrl} target="_blank" rel="noreferrer">
                        {finalQrUrl}
                      </a>
                    </Typography>
                  </Box>
                )}

                {/* Business Details */}
                <Box sx={{ mt: 3 }}>

                  <Typography variant="body2">
                    📞 Contact: +91 XXXXXXXXXX
                  </Typography>

                  <Typography variant="body2">
                    🌐 www.gadimitra.in
                  </Typography>

                  <Typography variant="body2">
                    📍 India
                  </Typography>

                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ mt: 2 }}
                  >
                    Scan QR to alert vehicle owner instantly
                  </Typography>

                </Box>

              </DialogContent>

              <DialogActions
                sx={{
                  justifyContent: 'center',
                  pb: 3,
                }}
              >

                <Button
                  onClick={handlePrint}
                  variant="contained"
                >
                  Print QR
                </Button>

                <Button
                  onClick={() => {
                    setSuccessOpen(false);
                    setShowUsers(true);
                  }}
                >
                  Close
                </Button>

              </DialogActions>

            </Dialog>

            <Dialog
              open={qrDialogOpen}
              onClose={() => setQrDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>
                Regenerate QR
              </DialogTitle>
              <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                <QRCodeCanvas value={finalQrUrl} size={220} includeMargin />
                <Typography variant="body1" sx={{ mt: 2, fontWeight: 700 }}>
                  Vehicle No: {normalizedVehicleNumber}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Scan this code to reach the alert page for the registered vehicle.
                </Typography>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                <Button onClick={handlePrint} variant="contained">
                  Print QR
                </Button>
                <Button onClick={() => setQrDialogOpen(false)}>
                  Close
                </Button>
              </DialogActions>
            </Dialog>

          </CardContent>

        </Card>

      ) : (

        <RegisteredUsers />

      )}

    </Box>
  );
};

export default RegistrationForm;